import {
  useGetAllCoursesLite,
  useGetAllMentors,
  useGetAllParents,
  useGetAllStudents,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Select, { SelectOption } from '@components/V4/Select/Select';
import RangePicker from '@components/V4/RangePicker/RangePicker';
import React, { ReactNode } from 'react';
import { Moment } from 'moment';

interface ParentsTableProps {
  searchParams: any;
  setSearchParams: (data: Partial<any>) => void;
  studentSearch: string;
  mentorSearch: string;
  parentSearch: string;
  setStudentSearch: React.Dispatch<React.SetStateAction<string>>;
  setMentorSearch: React.Dispatch<React.SetStateAction<string>>;
  setParentSearch: React.Dispatch<React.SetStateAction<string>>;
  debouncedStudentSearch: string;
  debouncedMentorSearch: string;
  debouncedParentSearch: string;
  title?: string;
  dateRangeValue?: [Moment, Moment] | null;
  onDateRangeChange?: (dates: [Moment | null, Moment | null] | null) => void;
  headerExtra?: ReactNode;
}

const ParentsHeader = (props: ParentsTableProps) => {
  const {
    searchParams,
    setSearchParams,
    studentSearch,
    mentorSearch,
    parentSearch,
    setStudentSearch,
    setMentorSearch,
    debouncedStudentSearch,
    debouncedMentorSearch,
    setParentSearch,
    debouncedParentSearch,
    title = 'Parent Feedback',
    dateRangeValue,
    onDateRangeChange,
    headerExtra,
  } = props;

  const { data: courses } = useGetAllCoursesLite();

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const { data: mentors, isLoading: isMentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    limit: 20,
  });

  const { data: parents, isLoading: isParentsLoading } = useGetAllParents({
    search: debouncedParentSearch,
    limit: 20,
  });

  const allOption = { label: 'ALL', value: null };

  const courseFilterChangeHandler = (courseId: string) => {
    setSearchParams({
      course: courseId,
      page: 1,
    });
  };

  const getCoursesOptions = () => {
    const courseOptions: SelectOption[] =
      courses?.map((course) => ({
        label: course.title,
        value: course._id,
      })) || [];

    if (!debouncedMentorSearch) {
      courseOptions.unshift(allOption);
    }

    return courseOptions;
  };

  const getMentorOptions = () => {
    const mentorOptions: SelectOption[] =
      mentors?.map((mentor) => ({
        label: mentor.fullName,
        value: mentor._id,
      })) || [];

    if (!debouncedMentorSearch) {
      mentorOptions.unshift(allOption);
    }

    return mentorOptions;
  };

  const getStudentOptions = () => {
    const studentOptions: SelectOption[] =
      students?.map((student) => ({
        label: student.fullName,
        value: student._id,
      })) || [];

    if (!debouncedStudentSearch) {
      studentOptions.unshift(allOption);
    }

    return studentOptions;
  };

  const getParentOptions = () => {
    const parentOptions: SelectOption[] =
      parents?.map((parent) => ({
        label: parent.parentData.fullName,
        value: parent._id,
      })) || [];

    if (!debouncedParentSearch) {
      parentOptions.unshift(allOption);
    }

    return parentOptions;
  };

  const studentFilterChangeHandler = (studentId: string) => {
    setSearchParams({ student: studentId, page: 1 });
  };

  const mentorFilterChangeHandler = (mentorId: string) => {
    setSearchParams({
      mentor: mentorId,
      page: 1,
    });
  };

  const parentFilterChangeHandler = (parentId: string) => {
    setSearchParams({ parent: parentId, page: 1 });
  };

  return (
    <section className="flex flex-col gap-6 bg-surfaceObject px-18 pt-8 pb-6">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h3 className="text-xl font-bold">{title}</h3>
        {headerExtra && <div className="min-w-[200px]">{headerExtra}</div>}
      </div>

      <div className="flex gap-4 flex-wrap">
        <Select
          className="w-2/6 max-w-56"
          label="Course"
          value={searchParams?.course}
          options={getCoursesOptions()}
          onSelect={(value) => courseFilterChangeHandler(value as string)}
          placeholder="Search Course"
          variant="secondary"
        />

        <Select
          className="w-1/6 max-w-56"
          label="Student"
          value={searchParams?.student}
          filterOption={() => true}
          searchValue={studentSearch}
          onSearch={setStudentSearch}
          onBlur={() => setStudentSearch('')}
          onSelect={(value) => studentFilterChangeHandler(value as string)}
          options={getStudentOptions()}
          notFoundContent={
            isStudentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Students"
          variant="secondary"
        />
        <Select
          className="w-1/6 max-w-56"
          label="Mentor"
          value={searchParams?.mentor}
          filterOption={() => true}
          searchValue={mentorSearch}
          onSearch={setMentorSearch}
          onSelect={(value) => mentorFilterChangeHandler(value as string)}
          onBlur={() => setMentorSearch('')}
          options={getMentorOptions()}
          notFoundContent={
            isMentorsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Mentor"
          variant="secondary"
        />

        <Select
          className="w-1/6 max-w-56"
          label="Parent"
          value={searchParams?.parent}
          filterOption={() => true}
          searchValue={parentSearch}
          onSearch={setParentSearch}
          onSelect={(value) => parentFilterChangeHandler(value as string)}
          onBlur={() => setParentSearch('')}
          options={getParentOptions()}
          notFoundContent={
            isParentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
          }
          placeholder="Search Parent"
          variant="secondary"
        />

        {onDateRangeChange && (
          <RangePicker
            className="min-w-[280px] w-full sm:w-2/6"
            label="Feedback Date Range"
            value={dateRangeValue ?? null}
            onChange={onDateRangeChange}
            variant="secondary"
            allowClear
            placeholder={['Start date', 'End date']}
          />
        )}
      </div>
    </section>
  );
};

export default ParentsHeader;
